import React from "react";
import * as Yup from "yup";
import {TranslatedText, getValidationRule} from "../../../helpers/helpers";
import {UserRoleEnum} from "../../../helpers/enums";

export const getSchemaUserPublic = () => {
  const validationEmail = getValidationRule("email");
  const valindationPhone = getValidationRule("phone");
  const valindationFiscalCode = getValidationRule("fiscalCode");
  const validationNameMaxChars = getValidationRule("nameMaximumNumberOfChar");
  const validationNameAllowChars = getValidationRule("nameAllowedChars");
  const validationSurnameMaxChars = getValidationRule("surnameMaximumNumberOfChar");
  const validationSurnameAllowChars = getValidationRule("surnameAllowedChars");
  const validationPhoneMobileAllowedChars = getValidationRule("phoneMobileAllowedChars");
  const validationPhoneMobileMinimumNumberOfChar = getValidationRule("phoneMobileMinimumNumberOfChar");
  const validationPhoneMobileMaximumNumberOfChar = getValidationRule("phoneMobileMaximumNumberOfChar");
  const validationPhoneLandlineAllowedChars = getValidationRule("phoneLandlineAllowedChars");
  const validationPhoneLandlineMinimumNumberOfChar = getValidationRule("phoneLandlineMinimumNumberOfChar");
  const validationPhoneLandlineMaximumNumberOfChar = getValidationRule("phoneLandlineMaximumNumberOfChar");
  const notEmpty = getValidationRule("notEmpty");

  return Yup.object().shape({
    name: Yup.string()
      .matches(validationNameMaxChars, {
        message: (
          <TranslatedText i18nKey="nameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationNameAllowChars, {
        message: <TranslatedText i18nKey="nameAllowedChars_error_message"/>,
      })
      .required(
        <TranslatedText i18nKey="errors.registration.public.name.required"/>
      ),
    surname: Yup.string()
      .matches(validationSurnameMaxChars, {
        message: (
          <TranslatedText i18nKey="surnameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationSurnameAllowChars, {
        message: <TranslatedText i18nKey="surnameAllowedChars_error_message"/>,
      })
      .required(
        <TranslatedText i18nKey="errors.registration.public.surname.required"/>
      ),
    email: Yup.string()
      .matches(validationEmail, {
        message: (
          <TranslatedText i18nKey="errors.registration.public.email.match"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.public.email.required"/>
      ),
    confirmEmail: Yup.string()
      .required(
        <TranslatedText i18nKey="errors.registration.public.confirmEmail.required"/>
      )
      .oneOf(
        [Yup.ref("email"), null],
        <TranslatedText i18nKey="errors.registration.public.confirmEmail.combine"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.public.confirmEmail.required.email"/>
      ),
    contactEmail: Yup.string().when("sameAsRegistrationEmail", {
      is: true,
      then: Yup.string().notRequired(),
      otherwise: Yup.string()
        .matches(validationEmail, {
          message: (
            <TranslatedText i18nKey="errors.registration.admin.contact.email.match"/>
          ),
        })
        .required(
          <TranslatedText i18nKey="errors.registration.admin.contact.email.required"/>
        ),
    }),
    pec: Yup.string().matches(validationEmail, {
      message: (
        <TranslatedText i18nKey="errors.registration.admin.pec.match"/>
      ),
    }),
    fiscalCode: Yup.string()
      .min(11, <TranslatedText i18nKey="errors.registration.public.fc.short"/>)
      .max(16, <TranslatedText i18nKey="errors.registration.public.fc.long"/>)
      .matches(valindationFiscalCode, {
        message: (
          <TranslatedText i18nKey="errors.registration.public.fc.match"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.public.fc.required"/>
      ),
    landlinePhoneNumber: Yup.string()
      .matches(notEmpty, {message: <TranslatedText i18nKey="notEmpty.error.message"/>})
      .matches(validationPhoneLandlineAllowedChars, {
        message: <TranslatedText i18nKey="phone_landline_not_valid_error_message"/>
      })
      .matches(validationPhoneLandlineMinimumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_landline_too_short_error_message"/>
      })
      .matches(validationPhoneLandlineMaximumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_landline_too_long_error_message"/>
      })
      .matches(valindationPhone, {
        message: (
          <TranslatedText i18nKey="phone.error.message"/>
        ),
      }),
    mobilePhoneNumber: Yup.string()
      .required(
        <TranslatedText i18nKey="errors.registration.public.cellphone.required"/>
      )
      .matches(notEmpty, {message: <TranslatedText i18nKey="notEmpty.error.message"/>})
      .matches(validationPhoneMobileAllowedChars, {
        message: <TranslatedText i18nKey="phone_mobile_not_valid_error_message"/>
      })
      .matches(validationPhoneMobileMinimumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_short_error_message"/>
      })
      .matches(validationPhoneMobileMaximumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_long_error_message"/>
      })
      .matches(valindationPhone, {
        message: (
          <TranslatedText i18nKey="phone.error.message"/>
        ),
      }),
  });
};

export const getSchemaUserCompany = () => {
  const validationEmail = getValidationRule("email");
  const valindationPhone = getValidationRule("phone");
  const valindationFiscalCode = getValidationRule("fiscalCode");
  const valindationCodeOrVat = getValidationRule("fiscalCodeOrVAT");
  const validationNameMaxChars = getValidationRule("nameMaximumNumberOfChar");
  const validationNameAllowChars = getValidationRule("nameAllowedChars");
  const validationSurnameMaxChars = getValidationRule(
    "surnameMaximumNumberOfChar"
  );
  const validationSurnameAllowChars = getValidationRule("surnameAllowedChars");
  const validationPhoneMobileAllowedChars = getValidationRule("phoneMobileAllowedChars");
  const validationPhoneMobileMinimumNumberOfChar = getValidationRule("phoneMobileMinimumNumberOfChar");
  const validationPhoneMobileMaximumNumberOfChar = getValidationRule("phoneMobileMaximumNumberOfChar");
  const validationPhoneLandlineAllowedChars = getValidationRule("phoneLandlineAllowedChars");
  const validationPhoneLandlineMinimumNumberOfChar = getValidationRule("phoneLandlineMinimumNumberOfChar");
  const validationPhoneLandlineMaximumNumberOfChar = getValidationRule("phoneLandlineMaximumNumberOfChar");
  const notEmpty = getValidationRule("notEmpty");

  return Yup.object().shape({
    name: Yup.string()
      // .min(
      //   2,
      //   <TranslatedText i18nKey="errors.registration.company.name.short" />
      // )
      // .max(
      //   50,
      //   <TranslatedText i18nKey="errors.registration.company.name.long" />
      // )
      .matches(validationNameMaxChars, {
        message: (
          <TranslatedText i18nKey="nameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationNameAllowChars, {
        message: <TranslatedText i18nKey="nameAllowedChars_error_message"/>,
      })

      .required(
        <TranslatedText i18nKey="errors.registration.company.name.required"/>
      ),
    surname: Yup.string()
      // .min(
      //   2,
      //   <TranslatedText i18nKey="errors.registration.company.surname.short" />
      // )
      // .max(
      //   50,
      //   <TranslatedText i18nKey="errors.registration.company.surname.long" />
      // )
      .matches(validationSurnameMaxChars, {
        message: (
          <TranslatedText i18nKey="surnameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationSurnameAllowChars, {
        message: <TranslatedText i18nKey="surnameAllowedChars_error_message"/>,
      })
      .required(
        <TranslatedText i18nKey="errors.registration.company.surname.required"/>
      ),
    email: Yup.string()
      .matches(validationEmail, {
        message: (
          <TranslatedText i18nKey="errors.registration.company.email.match"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.company.email.required"/>
      ),
    confirmEmail: Yup.string()
      .required(
        <TranslatedText i18nKey="errors.registration.company.confirmEmail.required"/>
      )
      .oneOf(
        [Yup.ref("email"), null],
        <TranslatedText i18nKey="errors.registration.company.confirmEmail.combine"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.company.confirmEmail.required.email"/>
      ),
    contactEmail: Yup.string().when("sameAsRegistrationEmail", {
      is: true,
      then: Yup.string().notRequired(),
      otherwise: Yup.string()
        .matches(validationEmail, {
          message: (
            <TranslatedText i18nKey="errors.registration.admin.contact.email.match"/>
          ),
        })
        .required(
          <TranslatedText i18nKey="errors.registration.admin.contact.email.required"/>
        ),
    }),
    fiscalCode: Yup.string()
      .min(11, <TranslatedText i18nKey="errors.registration.admin.fc.short"/>)
      .max(16, <TranslatedText i18nKey="errors.registration.admin.fc.long"/>)
      .test(
        "test-fiscalCode",
        <TranslatedText i18nKey="errors.registration.admin.fc.match"/>,
        (value) => {
          if (value === undefined) {
            return true;
          }
          if (value.length >= 12 && value.length <= 16) {
            const fiscalCodeRegex = new RegExp(valindationFiscalCode);
            return fiscalCodeRegex.test(value);
          }
          return true;
        }
      )
      .test(
        "test-Vat",
        <TranslatedText i18nKey="errors.registration.admin.piva.match"/>,
        (value) => {
          if (value === undefined) {
            return true;
          }
          if (value.length === 11) {
            const vatRegex = new RegExp(valindationCodeOrVat);
            return vatRegex.test(value);
          }
          return true;
        }
      )
      .required(
        <TranslatedText i18nKey="errors.registration.admin.fc.required"/>
      ),
    landlinePhoneNumber: Yup.string()
      .matches(notEmpty, {message: <TranslatedText i18nKey="notEmpty.error.message"/>})
      .matches(validationPhoneLandlineAllowedChars, {
        message: <TranslatedText i18nKey="phone_landline_not_valid_error_message"/>
      })
      .matches(validationPhoneLandlineMinimumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_landline_too_short_error_message"/>
      })
      .matches(validationPhoneLandlineMaximumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_landline_too_long_error_message"/>
      })
      .matches(valindationPhone, {
        message: (
          <TranslatedText i18nKey="phone.error.message"/>
        ),
      }),
    mobilePhoneNumber: Yup.string()
      .matches(notEmpty, {message: <TranslatedText i18nKey="notEmpty.error.message"/>})
      .matches(validationPhoneMobileAllowedChars, {
        message: <TranslatedText i18nKey="phone_mobile_not_valid_error_message"/>
      })
      .matches(validationPhoneMobileMinimumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_short_error_message"/>
      })
      .matches(validationPhoneMobileMaximumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_long_error_message"/>
      })
      .matches(valindationPhone, {
        message: (
          <TranslatedText i18nKey="phone.error.message"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.company.cellphone.required"/>
      ),
    pec: Yup.string().matches(validationEmail, {
      message: (
        <TranslatedText i18nKey="errors.registration.company.pec.match"/>
      ),
    }),
  });
};

export const getSchemaUserBuildingAdmin = () => {
  const validationEmail = getValidationRule("email");
  const valindationPhone = getValidationRule("phone");
  const valindationFiscalCode = getValidationRule("fiscalCode");
  const valindationCodeOrVat = getValidationRule("fiscalCodeOrVAT");
  const validationNameMaxChars = getValidationRule("nameMaximumNumberOfChar");
  const validationNameAllowChars = getValidationRule("nameAllowedChars");
  const validationSurnameMaxChars = getValidationRule(
    "surnameMaximumNumberOfChar"
  );
  const validationSurnameAllowChars = getValidationRule("surnameAllowedChars");
  const validationPhoneMobileAllowedChars = getValidationRule("phoneMobileAllowedChars");
  const validationPhoneMobileMinimumNumberOfChar = getValidationRule("phoneMobileMinimumNumberOfChar");
  const validationPhoneMobileMaximumNumberOfChar = getValidationRule("phoneMobileMaximumNumberOfChar");
  const notEmpty = getValidationRule("notEmpty");

  return Yup.object().shape({
    name: Yup.string()
      // .min(2, <TranslatedText i18nKey="errors.registration.admin.name.short" />)
      // .max(50, <TranslatedText i18nKey="errors.registration.admin.name.long" />)
      .matches(validationNameMaxChars, {
        message: (
          <TranslatedText i18nKey="nameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationNameAllowChars, {
        message: <TranslatedText i18nKey="nameAllowedChars_error_message"/>,
      })
      .required(
        <TranslatedText i18nKey="errors.registration.admin.name.required"/>
      ),
    surname: Yup.string()
      // .min(
      //   2,
      //   <TranslatedText i18nKey="errors.registration.admin.surname.short" />
      // )
      // .max(
      //   50,
      //   <TranslatedText i18nKey="errors.registration.admin.surname.long" />
      // )

      .matches(validationSurnameMaxChars, {
        message: (
          <TranslatedText i18nKey="surnameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationSurnameAllowChars, {
        message: <TranslatedText i18nKey="surnameAllowedChars_error_message"/>,
      })

      .required(
        <TranslatedText i18nKey="errors.registration.admin.surname.required"/>
      ),
    contactEmail: Yup.string()
      .matches(validationEmail, {
        message: (
          <TranslatedText i18nKey="errors.registration.admin.contact.email.match"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.admin.contact.email.required"/>
      ),
    email: Yup.string()
      .matches(validationEmail, {
        message: (
          <TranslatedText i18nKey="errors.registration.admin.email.match"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.admin.email.required"/>
      ),
    confirmEmail: Yup.string()
      .required(
        <TranslatedText i18nKey="errors.registration.admin.confirmEmail.required"/>
      )
      .oneOf(
        [Yup.ref("email"), null],
        <TranslatedText i18nKey="errors.registration.admin.confirmEmail.combine"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.admin.confirmEmail.required.email"/>
      ),
    fiscalCode: Yup.string()
      .min(11, <TranslatedText i18nKey="errors.registration.admin.fc.short"/>)
      .max(16, <TranslatedText i18nKey="errors.registration.admin.fc.long"/>)
      // .matches(valindationFiscalCode, {message:<TranslatedText i18nKey="errors.registration.admin.fc.match"/>})
      // .matches(valindationCodeOrVat, {message:<TranslatedText i18nKey="errors.registration.admin.piva.match"/>})
      .test(
        "test-fiscalCode",
        <TranslatedText i18nKey="errors.registration.admin.fc.match"/>,
        (value) => {
          if (value === undefined) {
            return true;
          }
          if (value.length >= 12 && value.length <= 16) {
            //const fiscalCodeRegex = /^(?:[A-Z][AEIOU][AEIOUX]|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/;
            const fiscalCodeRegex = new RegExp(valindationFiscalCode);
            return fiscalCodeRegex.test(value);
          }
          return true;
        }
      )
      .matches(/^[^89]/, {
        message: (
         <TranslatedText i18nKey="errors.registration.admin.fc.match"/>
        ),
      })
      .test(
        "test-Vat",
        <TranslatedText i18nKey="errors.registration.admin.piva.match"/>,
        (value) => {
          if (value === undefined) {
            return true;
          }
          if (value.length === 11) {
            //const vatRegex1 = /^([A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST]{1}[0-9LMNPQRSTUV]{2}[A-Z]{1}[0-9LMNPQRSTUV]{3}[A-Z]{1})$|([0-9]{11})$/;
            const vatRegex = new RegExp(valindationCodeOrVat);
            return vatRegex.test(value);
          }
          return true;
        }
      )
      .required(
        <TranslatedText i18nKey="errors.registration.admin.fc.required"/>
      ),
    pec: Yup.string().matches(validationEmail, {
      message: <TranslatedText i18nKey="errors.registration.admin.pec.match"/>,
    }),
    mobilePhoneNumber: Yup.string()
      .matches(notEmpty, {message: <TranslatedText i18nKey="notEmpty.error.message"/>})
      .matches(validationPhoneMobileAllowedChars, {
        message: <TranslatedText i18nKey="phone_mobile_not_valid_error_message"/>
      })
      .matches(validationPhoneMobileMinimumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_short_error_message"/>
      })
      .matches(validationPhoneMobileMaximumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_long_error_message"/>
      })
      .matches(valindationPhone, {
        message: (
          <TranslatedText i18nKey="phone.error.message"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.admin.cellphone.required"/>
      ),
    legalAddress_street: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.admin.legal.street.required"/>
    ),
    legalAddress_streetNumber: Yup.number()
      .typeError(
        <TranslatedText i18nKey="errors.registration.admin.legal.streetNumber.error"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.admin.legal.streetNumber.required"/>
      ),
    legalAddress_postalCode: Yup.number()
      .typeError(
        <TranslatedText i18nKey="errors.registration.admin.legal.postalcode.error"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.admin.legal.postalcode.required"/>
      ),
    legalAddress_city: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.admin.legal.city.required"/>
    ),
    legalAddress_province: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.admin.legal.province.required"/>
    ),
    headOfficeAddress_street: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.admin.office.street.required"/>
    ),
    headOfficeAddress_streetNumber: Yup.number()
      .typeError(
        <TranslatedText i18nKey="errors.registration.admin.office.streetNumber.error"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.admin.office.streetNumber.required"/>
      ),
    headOfficeAddress_postalCode: Yup.number()
      .typeError(
        <TranslatedText i18nKey="errors.registration.admin.office.postcode.error"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.admin.office.postcode.required"/>
      ),
    headOfficeAddress_city: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.admin.office.city.required"/>
    ),
    headOfficeAddress_province: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.admin.office.province.required"/>
    ),
    businessName: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.admin.office.businesName.required"/>
    ),
  });
};
export const getSchemaUserMunicipalFieldCustomerCity = () => {
  return Yup.object().shape({
    customerCity: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.municipal.province.required"/>
    ),
  })
};
export const getSchemaUserMunicipal = () => {
  const validationEmail = getValidationRule("email");
  const valindationPhone = getValidationRule("phone");
  const valindationFiscalCode = getValidationRule("fiscalCode");
  const valindationCodeOrVat = getValidationRule("fiscalCodeOrVAT");
  const validationNameMaxChars = getValidationRule("nameMaximumNumberOfChar");
  const validationNameAllowChars = getValidationRule("nameAllowedChars");
  const validationSurnameMaxChars = getValidationRule(
    "surnameMaximumNumberOfChar"
  );
  const validationSurnameAllowChars = getValidationRule("surnameAllowedChars");
  const validationPhoneMobileAllowedChars = getValidationRule("phoneMobileAllowedChars");
  const validationPhoneMobileMinimumNumberOfChar = getValidationRule("phoneMobileMinimumNumberOfChar");
  const validationPhoneMobileMaximumNumberOfChar = getValidationRule("phoneMobileMaximumNumberOfChar");
  const validationPhoneLandlineAllowedChars = getValidationRule("phoneLandlineAllowedChars");
  const validationPhoneLandlineMinimumNumberOfChar = getValidationRule("phoneLandlineMinimumNumberOfChar");
  const validationPhoneLandlineMaximumNumberOfChar = getValidationRule("phoneLandlineMaximumNumberOfChar");
  const notEmpty = getValidationRule("notEmpty");

  return Yup.object().shape({
    name: Yup.string()
      // .min(
      //   2,
      //   <TranslatedText i18nKey="errors.registration.municipal.name.short" />
      // )
      // .max(
      //   50,
      //   <TranslatedText i18nKey="errors.registration.municipal.name.long" />
      // )
      .matches(validationNameMaxChars, {
        message: (
          <TranslatedText i18nKey="nameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationNameAllowChars, {
        message: <TranslatedText i18nKey="nameAllowedChars_error_message"/>,
      })

      .required(
        <TranslatedText i18nKey="errors.registration.municipal.name.required"/>
      ),
    surname: Yup.string()
      // .min(
      //   2,
      //   <TranslatedText i18nKey="errors.registration.municipal.surname.short" />
      // )
      // .max(
      //   50,
      //   <TranslatedText i18nKey="errors.registration.municipal.surname.long" />
      // )
      .matches(validationSurnameMaxChars, {
        message: (
          <TranslatedText i18nKey="surnameMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validationSurnameAllowChars, {
        message: <TranslatedText i18nKey="surnameAllowedChars_error_message"/>,
      })
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.surname.required"/>
      ),
    email: Yup.string()
      .matches(validationEmail, {
        message: (
          <TranslatedText i18nKey="error.registration.municipal.email.match"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.email.required"/>
      ),
    confirmEmail: Yup.string()
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.confirmEmail.required"/>
      )
      .oneOf(
        [Yup.ref("email"), null],
        <TranslatedText i18nKey="errors.registration.municipal.confirmEmail.combine"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.confirmEmail.required.email"/>
      ),
    fiscalCode: Yup.string()
      .min(
        11,
        <TranslatedText i18nKey="errors.registration.municipal.fc.short"/>
      )
      .max(
        16,
        <TranslatedText i18nKey="errors.registration.municipal.fc.long"/>
      )
      .test(
        "test-fiscalCode",
        <TranslatedText i18nKey="errors.registration.municipal.fc.match"/>,
        (value) => {
          if (value === undefined) {
            return true;
          }
          if (value.length >= 12 && value.length <= 16) {
            //const fiscalCodeRegex = /^(?:[A-Z][AEIOU][AEIOUX]|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/;
            const fiscalCodeRegex = new RegExp(valindationFiscalCode);
            return fiscalCodeRegex.test(value);
          }
          return true;
        }
      )
      .test(
        "test-Vat",
        <TranslatedText i18nKey="errors.registration.municipal.piva.match"/>,
        (value) => {
          if (value === undefined) {
            return true;
          }
          if (value.length === 11) {
            //const vatRegex = /^([A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST]{1}[0-9LMNPQRSTUV]{2}[A-Z]{1}[0-9LMNPQRSTUV]{3}[A-Z]{1})$|([0-9]{11})$/;
            const vatRegex = new RegExp(valindationCodeOrVat);
            return vatRegex.test(value);
          }
          return true;
        }
      )
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.fc.required"/>
      ),
    pec: Yup.string().matches(validationEmail, {
      message: (
        <TranslatedText i18nKey="errors.registration.municipal.pec.required"/>
      ),
    }),
    landlinePhoneNumber: Yup.string()
      .matches(notEmpty, {message: <TranslatedText i18nKey="notEmpty.error.message"/>})
      .matches(validationPhoneLandlineAllowedChars, {
        message: <TranslatedText i18nKey="phone_landline_not_valid_error_message"/>
      })
      .matches(validationPhoneLandlineMinimumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_landline_too_short_error_message"/>
      })
      .matches(validationPhoneLandlineMaximumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_landline_too_long_error_message"/>
      })
      .matches(valindationPhone, {
        message: (
          <TranslatedText i18nKey="phone.error.message"/>
        ),
      }),
    mobilePhoneNumber: Yup.string()
      .matches(notEmpty, {message: <TranslatedText i18nKey="notEmpty.error.message"/>})
      .matches(validationPhoneMobileAllowedChars, {
        message: <TranslatedText i18nKey="phone_mobile_not_valid_error_message"/>
      })
      .matches(validationPhoneMobileMinimumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_short_error_message"/>
      })
      .matches(validationPhoneMobileMaximumNumberOfChar, {
        message: <TranslatedText i18nKey="phone_mobile_too_long_error_message"/>
      })
      .matches(valindationPhone, {
        message: (
          <TranslatedText i18nKey="phone.error.message"/>
        ),
      })
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.cellphone.required"/>
      ),
    legalAddress_street: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.municipal.street.required"/>
    ),
    legalAddress_streetNumber: Yup.number()
      .typeError(
        <TranslatedText i18nKey="errors.registration.municipal.streetNumber.error"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.streetNumber.required"/>
      ),
    legalAddress_postalCode: Yup.number()
      .typeError(
        <TranslatedText i18nKey="errors.registration.municipal.postcode.error"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.municipal.postcode.required"/>
      ),
    legalAddress_city: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.municipal.city.required"/>
    ),
    legalAddress_province: Yup.string().required(
      <TranslatedText i18nKey="errors.registration.municipal.province.required"/>
    ),
    // agreementPrivacy: Yup.bool().oneOf([true], 'Il consenso è necessario per la registrazione'),
    // agreementMarketing: Yup.bool().oneOf([true], 'Accept Terms & Conditions for Marketing data'),
    // agreementProfiling: Yup.bool().oneOf([true], 'Accept Terms & Conditions for management profiling data')
  });
};

export const initialValuesUserPublic = {
  name: "",
  surname: "",
  role: UserRoleEnum.PRIVATE,
  password: "",
  confirmPassword: "",
  fiscalCode: "",
  email: "",
  confirmEmail: "",
  contactEmail: "",
  pec: "",
  sameAsRegistrationEmail: true,
  mobilePhoneNumber: "",
  landlinePhoneNumber: "",
  agreementMarketing: false,
  agreementProfiling: false,
  recaptcha: "",
};

export const initialValuesUserCompany = {
  name: "",
  surname: "",
  role: UserRoleEnum.COMPANY,
  password: "",
  confirmPassword: "",
  fiscalCode: "",
  email: "",
  confirmEmail: "",
  contactEmail: "",
  sameAsRegistrationEmail: true,
  pec: "",
  mobilePhoneNumber: "",
  landlinePhoneNumber: "",
  agreementMarketing: false,
  agreementProfiling: false,
  recaptcha: "",
};

export const initialValuesUserBuildingAdmin = {
  name: "",
  surname: "",
  role: UserRoleEnum.CONDOMINIUM_ADMIN,
  password: "",
  confirmPassword: "",
  fiscalCode: "",
  pec: "",
  contactEmail: "",
  email: "",
  confirmEmail: "",
  mobilePhoneNumber: "",
  landlinePhoneNumber: "",
  fax: "",
  agreementMarketing: false,
  agreementProfiling: false,
  legalAddress_street: "",
  legalAddress_streetNumber: "",
  legalAddress_postalCode: "",
  legalAddress_city: "",
  legalAddress_province: "",
  headOfficeAddress_street: "",
  headOfficeAddress_streetNumber: "",
  headOfficeAddress_postalCode: "",
  headOfficeAddress_city: "",
  headOfficeAddress_province: "",
  businessName: "",
  recaptcha: "",
};

export const initialValuesMunicipalUser = {
  name: "",
  surname: "",
  role: UserRoleEnum.MUNICIPAL_USER,
  password: "",
  confirmPassword: "",
  email: "",
  confirmEmail: "",
  fiscalCode: "",
  pec: "",
  mobilePhoneNumber: "",
  landlinePhoneNumber: "",
  agreementMarketing: false,
  agreementProfiling: false,
  legalAddress_street: "",
  legalAddress_streetNumber: "",
  legalAddress_postalCode: "",
  legalAddress_city: "",
  legalAddress_province: "",
  customerCity: "",
  recaptcha: "",
};

export const getSchemaValidationPassword = () => {
  const validPasswordMinNumber = getValidationRule(
    "passwordMinimumNumberOfChar"
  );
  const validPaswordUppercase = getValidationRule("passwordUppercaseChar");
  const validPaswordLowercase = getValidationRule("passwordLowercaseChar");
  const validPaswordNumeric = getValidationRule("passwordNumericChar");
  const validPaswordSpecial = getValidationRule("passwordSpecialChar");
  const validPasswordMaxChart = getValidationRule(
    "passwordMaximumNumberOfChar"
  );
  return Yup.object().shape({
    password: Yup.string()
      .matches(validPasswordMaxChart, {
        message: (
          <TranslatedText i18nKey="passwordMaximumNumberOfChar_error_message"/>
        ),
      })
      .matches(validPasswordMinNumber, {
        message: (
          <TranslatedText i18nKey="errors.registration.password.match"/>
        ),
      })
      .matches(validPaswordUppercase, {
        message: (
          <TranslatedText i18nKey="errors.registration.password.match"/>
        ),
      })
      .matches(validPaswordLowercase, {
        message: (
          <TranslatedText i18nKey="errors.registration.password.match"/>
        ),
      })
      .matches(validPaswordNumeric, {
        message: (
          <TranslatedText i18nKey="errors.registration.password.match"/>
        ),
      })
      .matches(validPaswordSpecial, {
        message: (
          <TranslatedText i18nKey="errors.registration.password.match"/>
        ),
      })
      .required(<TranslatedText i18nKey="errors.registration.password.required"/>),
    confirmPassword: Yup.string()
      .required(<TranslatedText i18nKey="errors.registration.confirmPassword.required"/>)
      .oneOf(
        [Yup.ref("password"), null],
        <TranslatedText i18nKey="errors.registration.confirmPassword.combine"/>
      )
      .required(
        <TranslatedText i18nKey="errors.registration.confirmPassword.required.password"/>
      ),
  });
};

export const getSchemaValidationCaptcha = () => {
  return Yup.object().shape({
    recaptcha: Yup.string().required(
      <TranslatedText i18nKey="common.required.input"/>
    ),
  });
};

export const getSchemaValidationPrivacy = () => {
  return Yup.object().shape({
    agreementMarketing: Yup.bool().oneOf([false], <TranslatedText i18nKey="errors.agreementMarketing"/> ),
    agreementProfiling:  Yup.bool().oneOf([false], <TranslatedText i18nKey="errors.agreementProfiling"/> ),
  });
}
